import { DocumentNode } from 'graphql';
import { graphql, OptionProps } from 'react-apollo';
import configureApolloClient from '../../apollo/configureApolloClient';
import { ActivityListQuery, ActivityQuery, AthleteQuery } from './queries';
import {
    ActivityListInput,
    ActivityListProps,
    ActivityPayload,
    AthletePayload
} from './types';
import {
    DEFAULT_RESOLUTION,
    fetchActivityStreams
} from './withActivityStreams';

export const DEFAULT_PAGE_SIZE = 12;

export async function fetchActivityListFirstPage() {
    const client = configureApolloClient();

    return client.query({
        query: ActivityListQuery,
        variables: {
            input: {
                pageSize: DEFAULT_PAGE_SIZE
            }
        }
    });
}

/**
 * Fetches activity data from the server when the user mouses over
 * an activity in the activity list
 */
async function prefetchActivity(id: string) {
    // We don't want to prefetch data on the server, so make sure
    // we're in the browser
    if ((process as any).browser) {
        const client = configureApolloClient();

        // First fetch the activity data
        await client.query({
            query: ActivityQuery,
            variables: {
                input: { id: id.toString() }
            }
        });

        // Then get the athlete data, which will normally
        // be cached already
        const {
            data: { loggedInUser }
        } = await client.query<AthletePayload>({
            query: AthleteQuery
        });

        // Last, fetch the activity stream data with the correct
        // arguments taken from the athlete info
        if (loggedInUser && loggedInUser.user && loggedInUser.user.athlete) {
            const athlete = loggedInUser.user.athlete;

            fetchActivityStreams({
                id,
                resolution: athlete.resolution || DEFAULT_RESOLUTION,
                selectedStreamType: athlete.selectedActivityStreamType,
                types: athlete.activityStreamTypes
            });
        }
    }
}

/** Fetches more activities, to allow infinite scrolling through the list */
const fetchMoreActivities = (
    fetchMore: (
        options: any
    ) => Promise<any>,
    pageInfo: { page: number; pageSize: number; hasMore: boolean },
    query: DocumentNode,
    queryName: string
) => (page: number) => {
    return fetchMore({
        query,
        updateQuery: (
            previousQueryResult: any,
            { fetchMoreResult }: { fetchMoreResult?: any }
        ) => {
            const prevPayload: { activities?: ActivityPayload[] } =
                previousQueryResult[queryName];
            const prevActivities =
                (prevPayload && prevPayload.activities) || [];

            const payload: {
                activities?: ActivityPayload[];
                pageInfo?: {
                    page: number;
                    pageSize: number;
                };
                __typename: string;
            } =
                fetchMoreResult && fetchMoreResult[queryName];

            const activities = (payload && payload.activities) || [];

            const results = {
                [queryName]: {
                    __typename: payload.__typename,
                    activities: [...prevActivities, ...activities],
                    pageInfo: payload.pageInfo
                }
            };

            return results;
        },
        variables: {
            input: {
                page: page + 1,
                pageSize: pageInfo.pageSize
            }
        }
    });
};

/**
 * Populates a component with a list of activities
 * @param WrappedComponent Component to be provided with an activity list
 */
export const withActivityListCore = <TProps extends {}>(
    query: DocumentNode,
    queryName: string,
    prefetch?: (id: string) => void
) => {
    return graphql<
        TProps & ActivityListInput,
        any,
        { input: { page?: number; pageSize: number } },
        TProps & ActivityListProps
    >(query, {
        options: (props: ActivityListInput & TProps) => {
            return {
                variables: {
                    input: {
                        page: props.page,
                        pageSize: props.pageSize || DEFAULT_PAGE_SIZE
                    }
                }
            };
        },
        props: (
            props: OptionProps<ActivityListInput & TProps, any>
        ): TProps & ActivityListProps => {
            const { data, ownProps } = props;

            if (data) {
                const { error, loading, fetchMore } = data;

                const payload = data[queryName] as {
                    activities?: ActivityPayload[];
                    pageInfo?: {
                        page: number;
                        pageSize: number;
                    };
                };

                const activities = (payload && payload.activities) || undefined;
                const pageSize = ownProps.pageSize || DEFAULT_PAGE_SIZE;

                const pageInfo = {
                    hasMore: !!(
                        payload &&
                        payload.pageInfo &&
                        payload.pageInfo.pageSize &&
                        payload.pageInfo.pageSize >= pageSize
                    ),
                    page:
                        payload && payload.pageInfo
                            ? payload.pageInfo.page
                            : ownProps.page || 1,
                    pageSize
                };

                return {
                    ...(ownProps as any),
                    activities,
                    error,
                    handleFetchMore: fetchMoreActivities(
                        fetchMore,
                        pageInfo,
                        query,
                        queryName
                    ),
                    loading,
                    pageInfo,
                    prefetchActivity: prefetch
                };
            }
            throw new Error('No activities provided');
        }
    });
};

export default withActivityListCore(
    ActivityListQuery,
    'activityList',
    prefetchActivity
);



// WEBPACK FOOTER //
// ./src/client/components/Strava/withActivityList.tsx